// ============================================================
// Настройка пула карт и модов (вынесена из рандомайзера и
// переиспользуется «Менеджером матча»).
// Слева — включение в розыгрыш и фильтр списка (деревом),
// справа — крупный список карт или горизонтальная панель модов.
// Исключённые id и флаг «пропускать дисбалансные» хранит вызывающая
// страница: модуль только показывает состояние и меняет его через колбэки.
// ============================================================

import { t } from '../i18n';
import { hideMapPreview, showMapPreview } from './map-preview';
import {
    LEAGUE_MAP_TYPE,
    MAP_FILTER_TYPES,
    MAP_STYLE_TYPES,
    isMapStyleType,
    leagueMapTypes,
    mapTeamFormats,
    mapTypesWithLeague,
    teamFormatLabel,
    type MapFilterType,
} from './map-presets';
import {
    MAP_TYPES,
    TEAM_FORMATS,
    type GameMap,
    type GameMod,
    type MapType,
    type TeamFormat,
} from '../api/game-refs';

/** Пресет пулов (таблица map_presets) — модулю нужны только id карт/модов и признак общего. */
export interface PoolPreset {
    id: number;
    name: string;
    shared: boolean;
    map_ids: number[];
    mod_ids: number[];
}

export interface PoolItem {
    id: number;
    name: string;
    title?: string | undefined;
    /** Типы карты (только для карт) — карта может относиться сразу к нескольким. */
    mapTypes?: MapType[];
    /** Форматы командной карты (только для карт с типом team). */
    teamFormats?: TeamFormat[];
    /** Число игроков карты (только для карт). */
    playerCount?: number | null;
    /** Публичный URL картинки карты (только для карт). */
    imageUrl?: string | null;
    /** Ссылка на карту (только для карт). */
    link?: string | null;
    /** Пометка «дисбалансная карта» (только для карт). */
    unbalanced?: boolean;
}

export type PoolKind = 'maps' | 'mods';

export interface PoolPickerOptions {
    kind: PoolKind;
    items: PoolItem[];
    presets: PoolPreset[];
    /** Текущие исключённые id (модуль не хранит их сам). */
    getExcluded: () => number[];
    /** Применяет новый набор исключённых. */
    setExcluded: (ids: number[]) => void;
    /** Вызывается после изменения пула (перерисовать барабаны/результат). */
    onChange?: () => void;
    /** Галочка «пропускать дисбалансные» (только карты). */
    skipUnbalanced?: { get: () => boolean; set: (value: boolean) => void };
    /** Показывать переключатель «Список/Плитка» (только карты). */
    viewSwitch?: boolean;
}

export interface PoolPicker {
    el: HTMLElement;
    /** Перечитывает внешнее состояние (исключённые, флаг дисбалансных). */
    refresh: () => void;
}

/** Карта справочника → элемент пула. */
export const mapToPoolItem = (m: GameMap): PoolItem => ({
    id: m.id,
    name: m.name,
    title: m.alt_name ?? undefined,
    mapTypes: m.map_types ?? [],
    teamFormats: mapTeamFormats(m),
    playerCount: m.player_count ?? null,
    imageUrl: m.image_url ?? null,
    link: m.link ?? null,
    unbalanced: Boolean(m.unbalanced),
});

/** Мод справочника → элемент пула (служебный NONE показываем как «Без мода»). */
export const modToPoolItem = (m: GameMod): PoolItem => ({
    id: m.id,
    name: m.name.trim().toUpperCase() === 'NONE' ? t('tools.random_no_mod') : m.name,
});

/** Ветвь дерева типов: категория (Формат / Стиль игры / Лига / Игроков) и её листья. */
interface TreeBranch {
    key: string;
    label: string;
    /** Подсказка для кнопок группы (тип или число игроков). */
    hint: string;
    leaves: {
        key: string;
        label: string;
        title?: string;
        /** id карт группы (режим включения в розыгрыш). */
        ids?: number[];
        /** Состояние и переключение фильтра (режим фильтра списка). */
        isActive?: () => boolean;
        toggle?: () => void;
    }[];
}

/** Вид списка карт: подробный список или компактная плитка из одних картинок. */
type MapView = 'list' | 'grid';
const VIEW_KEY = 'tools.random.view';
let mapView: MapView = loadMapView();
/** Ссылки на список и кнопки переключения вида — чтобы менять вид без перерисовки пула. */
let mapListEl: HTMLElement | null = null;
let viewButtons: { btn: HTMLButtonElement; mode: MapView }[] = [];

/** Свёрнутые ветви дерева типов (ключ вида `filter:format`) — переживают перерисовку пула. */
const collapsedTreeBranches = new Set<string>();

function loadMapView(): MapView {
    try {
        return localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list';
    } catch {
        return 'list';
    }
}

/** Переключает вид списка карт и запоминает выбор. */
function setMapView(mode: MapView): void {
    mapView = mode;
    try {
        localStorage.setItem(VIEW_KEY, mode);
    } catch {
        /* приватный режим — просто не запоминаем */
    }
    mapListEl?.classList.toggle('rr-map-list--grid', mode === 'grid');
    for (const item of viewButtons) item.btn.classList.toggle('is-active', item.mode === mode);
}

/** Кнопка переключения вида списка карт. */
function makeViewButton(mode: MapView, icon: string, text: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rr-view-btn' + (mapView === mode ? ' is-active' : '');
    btn.title = text;
    const i = document.createElement('span');
    i.className = 'rr-view-btn-icon';
    i.textContent = icon;
    const label = document.createElement('span');
    label.textContent = text;
    btn.append(i, label);
    btn.addEventListener('click', () => setMapView(mode));
    viewButtons.push({ btn, mode });
    return btn;
}

/** Переключатель вида списка карт: подробный список / компактная плитка. */
function buildViewSwitch(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'rr-view-switch';
    const label = document.createElement('span');
    label.className = 'rr-view-switch-label';
    label.textContent = t('tools.random_view_label');
    bar.append(
        label,
        makeViewButton('list', '☰', t('tools.random_view_list')),
        makeViewButton('grid', '▦', t('tools.random_view_grid'))
    );
    return bar;
}

/**
 * Секция панели пула: заголовок + пояснение, на что она влияет.
 * `onReset` добавляет в шапку крестик — снять все настройки этой секции.
 */
function buildPoolSection(
    titleText: string,
    noteText: string,
    modifier: string,
    onReset?: () => void,
    resetTitle?: string
): HTMLElement {
    const section = document.createElement('div');
    section.className = `rr-pool-section ${modifier}`;

    const head = document.createElement('div');
    head.className = 'rr-pool-section-head';
    const title = document.createElement('span');
    title.className = 'rr-pool-section-title';
    title.textContent = titleText;
    const note = document.createElement('span');
    note.className = 'rr-pool-section-note';
    note.textContent = noteText;
    head.append(title, note);

    if (onReset) {
        const reset = document.createElement('button');
        reset.type = 'button';
        reset.className = 'rr-section-reset';
        reset.textContent = '✕';
        reset.title = resetTitle ?? t('tools.random_section_reset');
        reset.addEventListener('click', onReset);
        head.appendChild(reset);
    }

    section.appendChild(head);
    return section;
}

/** Строка карты в пуле: миниатюра, название, число игроков и типы. */
function buildMapRow(
    it: PoolItem,
    presets: PoolPreset[],
    onToggle: () => void
): HTMLElement {
    const types = mapTypesWithLeague(it, leagueMapTypes(presets));
    const formats = it.teamFormats ?? [];
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'rr-map-row';
    row.dataset.id = String(it.id);
    // Подсказка нужна в плитке, где видно только картинку
    row.title = [
        it.name,
        it.title ?? '',
        it.playerCount != null ? t('tools.random_players', { n: it.playerCount }) : '',
        it.unbalanced ? t('tools.random_unbalanced_badge') : '',
        types.map((type) => t(`map_type.${type}` as any)).join(' · '),
        formats.map(teamFormatLabel).join(' · '),
    ]
        .filter(Boolean)
        .join('\n');

    const thumb = document.createElement('span');
    thumb.className = 'rr-map-row-thumb';
    if (it.imageUrl) {
        const img = document.createElement('img');
        img.src = it.imageUrl;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        thumb.addEventListener('mouseenter', () =>
            // В плитке название видно только в подсказке — дублируем его под картинкой
            showMapPreview(it.imageUrl!, thumb, mapView === 'grid' ? it.name : undefined)
        );
        thumb.addEventListener('mouseleave', hideMapPreview);
    } else {
        thumb.classList.add('is-empty');
        thumb.textContent = '🗺';
    }
    row.appendChild(thumb);

    const main = document.createElement('span');
    main.className = 'rr-map-row-main';
    const name = document.createElement('span');
    name.className = 'rr-map-row-name';
    name.textContent = it.name;
    main.appendChild(name);

    const meta = document.createElement('span');
    meta.className = 'rr-map-row-meta';
    if (it.unbalanced) {
        const b = document.createElement('span');
        b.className = 'rr-map-badge rr-map-badge--unbalanced';
        b.textContent = t('tools.random_unbalanced_badge');
        meta.appendChild(b);
    }
    if (it.playerCount != null) {
        const p = document.createElement('span');
        p.className = 'rr-map-badge';
        p.textContent = t('tools.random_players', { n: it.playerCount });
        meta.appendChild(p);
    }
    for (const type of types) {
        const b = document.createElement('span');
        b.className = 'rr-map-badge rr-map-badge--type' + (isMapStyleType(type) ? ' rr-map-badge--style' : '');
        b.textContent = t(`map_type.${type}` as any);
        meta.appendChild(b);
    }
    // Формат командной карты
    for (const format of formats) {
        const b = document.createElement('span');
        b.className = 'rr-map-badge rr-map-badge--format';
        b.textContent = teamFormatLabel(format);
        meta.appendChild(b);
    }
    main.appendChild(meta);
    row.appendChild(main);

    const state = document.createElement('span');
    state.className = 'rr-map-row-state';
    row.appendChild(state);

    row.addEventListener('click', onToggle);
    return row;
}

/** Список чипов/строк пула: клик включает/исключает вариант из розыгрыша. */
export function buildPoolPicker(options: PoolPickerOptions): PoolPicker {
    const { kind, items, presets } = options;
    const isMap = kind === 'maps';

    const box = document.createElement('div');
    box.className = 'card rr-pool';

    // --- Шапка: заголовок, счётчик включённых, «Все/Ничего» ---
    const head = document.createElement('div');
    head.className = 'rr-pool-head';
    const h = document.createElement('span');
    h.className = 'rr-pool-title';
    const counter = document.createElement('span');
    counter.className = 'rr-pool-count';
    h.append(`${isMap ? t('tools.random_pool_maps') : t('tools.random_pool_mods')} `, counter);

    const actions = document.createElement('div');
    actions.className = 'rr-pool-actions';
    const allBtn = document.createElement('button');
    allBtn.type = 'button';
    allBtn.className = 'btn-secondary';
    allBtn.textContent = t('tools.random_all');
    allBtn.title = t('tools.random_all_hint');
    const noneBtn = document.createElement('button');
    noneBtn.type = 'button';
    noneBtn.className = 'btn-secondary';
    noneBtn.textContent = t('tools.random_none');
    noneBtn.title = t('tools.random_all_hint');
    actions.append(allBtn, noneBtn);
    head.append(h, actions);

    // --- Панель над списком (не прокручивается вместе со списком) ---
    const tools = document.createElement('div');
    tools.className = 'rr-pool-tools';

    const chips = document.createElement('div');
    chips.className = 'rr-chips';

    /** Счётчик «включено/всего» в кнопках-группах (обновляется в applyStates). */
    const groupToggles: { btn: HTMLButtonElement; ids: number[] }[] = [];
    /** Кнопки фильтра видимого списка (для сброса). */
    const filterBtns: { btn: HTMLButtonElement; isActive: () => boolean }[] = [];
    /** Две группы списка карт: включённые в розыгрыш сверху, остальные ниже. */
    const mapGroups: { wrap: HTMLElement; list: HTMLElement; count: HTMLElement }[] = [];
    /** Строки карт в исходном (алфавитном) порядке — для перегруппировки. */
    const mapRows: { id: number; row: HTMLElement }[] = [];

    // Мультивыборный поиск (только скрывает строки, розыгрыш не меняет).
    let query = '';
    let searchTypes: MapFilterType[] = [];
    let searchCounts: number[] = [];
    let searchFormats: TeamFormat[] = [];

    const isExcluded = (id: number): boolean => options.getExcluded().includes(id);
    const skipOn = (): boolean => (isMap ? options.skipUnbalanced?.get() ?? false : false);
    /** Карта участвует в розыгрыше с учётом пометки «дисбалансная». */
    const isSkippedUnbalanced = (it: PoolItem): boolean =>
        Boolean(it.unbalanced) && skipOn();
    const isEnabled = (it: PoolItem): boolean =>
        !isExcluded(it.id) && !isSkippedUnbalanced(it);

    // Вычисляемые типы (микро/макро/гибрид/«FFA Лига») — из общих списков лиги.
    const league = leagueMapTypes(presets);
    const matches = (it: PoolItem): boolean => {
        if (isMap) {
            const types = mapTypesWithLeague(it, league);
            if (query) {
                // Поиск по названию/альт. названию и по подписям типов (например, «микро»).
                const hay = `${it.name} ${it.title ?? ''} ${types
                    .map((type) => t(`map_type.${type}` as any))
                    .join(' ')} ${(it.teamFormats ?? []).map(teamFormatLabel).join(' ')}`.toLowerCase();
                if (!hay.includes(query)) return false;
            }
            if (searchTypes.length > 0 && !types.some((tp) => searchTypes.includes(tp))) return false;
            if (searchCounts.length > 0 && (it.playerCount == null || !searchCounts.includes(it.playerCount)))
                return false;
            // Формат командной карты (2 на 2, 3 на 3, …)
            if (searchFormats.length > 0 && !(it.teamFormats ?? []).some((f) => searchFormats.includes(f)))
                return false;
            return true;
        }
        if (query && !it.name.toLowerCase().includes(query) && !(it.title ?? '').toLowerCase().includes(query))
            return false;
        return true;
    };

    const isFiltered = (): boolean =>
        Boolean(query) ||
        (isMap &&
            (searchTypes.length > 0 || searchCounts.length > 0 || searchFormats.length > 0));

    const shownEl = document.createElement('span');
    shownEl.className = 'rr-pool-shown';

    const clearFiltersBtn = document.createElement('button');
    clearFiltersBtn.type = 'button';
    clearFiltersBtn.className = 'rr-pool-clear-filters';
    clearFiltersBtn.textContent = t('tools.random_filters_reset');
    clearFiltersBtn.title = t('tools.random_filters_reset_hint');
    clearFiltersBtn.addEventListener('click', () => {
        query = '';
        if (search) search.value = '';
        searchTypes = [];
        searchCounts = [];
        searchFormats = [];
        for (const f of filterBtns) f.btn.classList.toggle('is-active', f.isActive());
        applyVisibility();
    });

    const emptyEl = document.createElement('div');
    emptyEl.className = 'rr-pool-empty hidden';
    emptyEl.textContent = t('tools.random_no_matches');

    // Поиск — отдельной строкой над пулом карт: он не относится ни к фильтру
    // по типам, ни к включению групп, поэтому не живёт в колонке настроек.
    const searchBar = document.createElement('div');
    searchBar.className = 'rr-map-search-bar';

    let search: HTMLInputElement | null = null;
    /** Галочка «пропускать дисбалансные» (только для карт). */
    let skipToggle: HTMLInputElement | null = null;
    const searchRow = document.createElement('div');
    searchRow.className = 'rr-pool-search-row';
    search = document.createElement('input');
    search.type = 'search';
    search.className = 'rr-pool-search';
    search.placeholder = isMap ? t('tools.random_search') : t('tools.random_search_mod');
    search.addEventListener('input', () => {
        query = search!.value.trim().toLowerCase();
        applyVisibility();
    });
    searchRow.append(search, shownEl, clearFiltersBtn);
    if (isMap && options.skipUnbalanced && items.some((it) => it.unbalanced)) {
        // Дисбалансные карты по умолчанию не участвуют в розыгрыше
        const label = document.createElement('label');
        label.className = 'rr-skip-unbalanced';
        label.title = t('tools.random_skip_unbalanced_hint');
        skipToggle = document.createElement('input');
        skipToggle.type = 'checkbox';
        skipToggle.checked = skipOn();
        skipToggle.addEventListener('change', () => {
            options.skipUnbalanced!.set(Boolean(skipToggle?.checked));
            applyStates();
            applyVisibility();
            options.onChange?.();
        });
        const span = document.createElement('span');
        span.textContent = t('tools.random_skip_unbalanced');
        label.append(skipToggle, span);
        searchRow.appendChild(label);
    }
    if (isMap) searchBar.appendChild(searchRow);
    else tools.appendChild(searchRow);

    /** Пересчитывает видимость строк, счётчики и подписи кнопок «Все/Ничего». */
    function applyVisibility(): void {
        let shownCount = 0;
        chips.querySelectorAll<HTMLElement>('.rr-chip, .rr-map-row').forEach((c) => {
            const it = items.find((p) => p.id === Number(c.dataset.id));
            const ok = !it || matches(it);
            c.classList.toggle('hidden', !ok);
            if (ok) shownCount += 1;
        });

        const filtered = isFiltered();
        shownEl.textContent = t('tools.random_shown', {
            shown: shownCount,
            total: items.length,
        });
        shownEl.classList.toggle('is-filtered', filtered);
        clearFiltersBtn.classList.toggle('hidden', !filtered);
        allBtn.textContent = filtered ? t('tools.random_all_visible') : t('tools.random_all');
        noneBtn.textContent = filtered ? t('tools.random_none_visible') : t('tools.random_none');
        emptyEl.classList.toggle('hidden', !(items.length > 0 && shownCount === 0));

        // У групп «Выбрано / Остальные» — счётчик видимых и скрытие пустой группы
        for (const g of mapGroups) {
            const visible = [...g.list.children].filter((c) => !c.classList.contains('hidden')).length;
            g.count.textContent = String(visible);
            g.wrap.classList.toggle('hidden', visible === 0);
        }
    }

    /**
     * Раскладывает строки карт по группам: сначала включённые в розыгрыш,
     * затем исключённые (внутри группы — исходный алфавитный порядок).
     * Фильтр (applyVisibility) только скрывает строки, разделение сохраняется.
     */
    function applyOrder(): void {
        if (mapGroups.length === 0) return;
        for (const { id, row } of mapRows) {
            const target = isExcluded(id) ? mapGroups[1]!.list : mapGroups[0]!.list;
            target.appendChild(row); // appendChild перемещает уже вставленный узел
        }
    }

    const applyStates = (): void => {
        const enabledCount = items.filter(isEnabled).length;
        counter.textContent = `${enabledCount}/${items.length}`;
        counter.classList.toggle('is-zero', enabledCount === 0);
        chips.querySelectorAll<HTMLElement>('.rr-chip, .rr-map-row').forEach((c) => {
            c.classList.toggle('is-off', isExcluded(Number(c.dataset.id)));
        });
        // Дисбалансные карты, пропущенные галочкой, показываем приглушённо
        if (isMap) {
            chips.querySelectorAll<HTMLElement>('.rr-map-row').forEach((row) => {
                const it = items.find((p) => p.id === Number(row.dataset.id));
                row.classList.toggle('is-unbalanced-skip', Boolean(it && isSkippedUnbalanced(it)));
            });
        }
        // Кнопки-группы: выключена целиком / включена частично / включена вся
        for (const { btn, ids } of groupToggles) {
            const on = ids.filter((id) => !isExcluded(id)).length;
            btn.classList.toggle('is-off', on === 0);
            btn.classList.toggle('is-partial', on > 0 && on < ids.length);
            btn.setAttribute('aria-pressed', String(on > 0));
            const cnt = btn.querySelector('.rr-group-toggle-count');
            if (cnt) cnt.textContent = `${on}/${ids.length}`;
        }
    };

    const update = (): void => {
        applyStates();
        applyOrder(); // включённая карта переезжает в группу «Выбрано»
        applyVisibility(); // счётчики групп после перегруппировки
        options.onChange?.();
    };

    const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name));

    const appendChip = (parent: HTMLElement, it: PoolItem): void => {
        const c = document.createElement('button');
        c.type = 'button';
        c.className = 'rr-chip';
        c.dataset.id = String(it.id);
        c.textContent = it.name;
        if (it.title) c.title = it.title;
        c.addEventListener('click', () => {
            const list = options.getExcluded();
            const i = list.indexOf(it.id);
            options.setExcluded(
                i >= 0 ? list.filter((id) => id !== it.id) : [...list, it.id]
            );
            update();
        });
        parent.appendChild(c);
    };

    if (isMap) {
        // Полноценный список карт: миниатюра, число игроков, типы.
        chips.classList.add('rr-map-list');
        chips.classList.toggle('rr-map-list--grid', mapView === 'grid');
        mapListEl = chips;
        viewButtons = [];

        const counts = [...new Set(items.map((it) => it.playerCount).filter((n): n is number => n != null))]
            .sort((a, b) => a - b);
        const presentTypes = MAP_FILTER_TYPES.filter((type) =>
            items.some((it) => mapTypesWithLeague(it, league).includes(type))
        );

        // --- 1. Фильтр списка: только видимость строк, розыгрыш не меняет ---
        const filterSection = buildPoolSection(
            t('tools.random_filters_title'),
            t('tools.random_filters_note'),
            'rr-pool-section--filter',
            // Крестик: снять все включённые фильтры и поиск
            () => {
                query = '';
                if (search) search.value = '';
                searchTypes = [];
                searchCounts = [];
                searchFormats = [];
                for (const f of filterBtns) f.btn.classList.toggle('is-active', f.isActive());
                applyVisibility();
            },
            t('tools.random_section_reset')
        );

        // --- 2. Включение групп: разом вкл/выкл все карты типа или числа игроков ---
        const groupSection = buildPoolSection(
            t('tools.random_groups_title'),
            t('tools.random_groups_note'),
            'rr-pool-section--groups',
            // Крестик: исключить все карты из розыгрыша (снять все групповые включения)
            () => {
                options.setExcluded(items.map((it) => it.id));
                update();
            },
            t('tools.random_groups_reset')
        );

        /** Кнопка-группа со счётчиком включённых карт. */
        const makeGroupToggle = (label: string, ids: number[], hint: string): HTMLButtonElement => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'rr-group-toggle';
            btn.title = hint;
            const mark = document.createElement('span');
            mark.className = 'rr-group-toggle-mark';
            const name = document.createElement('span');
            name.className = 'rr-group-toggle-name';
            name.textContent = label;
            const cnt = document.createElement('span');
            cnt.className = 'rr-group-toggle-count';
            btn.append(mark, name, cnt);
            btn.addEventListener('click', () => {
                const current = options.getExcluded();
                const allOff = ids.every((id) => current.includes(id));
                options.setExcluded(
                    allOff
                        ? current.filter((id) => !ids.includes(id))
                        : [...new Set([...current, ...ids])]
                );
                update();
            });
            groupToggles.push({ btn, ids });
            return btn;
        };

        /** Чип фильтра: только показывает/скрывает строки (на розыгрыш не влияет). */
        const makeFilterChip = (
            label: string,
            isActive: () => boolean,
            toggle: () => void,
            title?: string
        ): HTMLButtonElement => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'rr-map-filter' + (isActive() ? ' is-active' : '');
            btn.textContent = label;
            if (title) btn.title = title;
            btn.addEventListener('click', () => {
                toggle();
                btn.classList.toggle('is-active', isActive());
                applyVisibility();
            });
            filterBtns.push({ btn, isActive });
            return btn;
        };

        /** id карт конкретного типа (с учётом вычисляемых типов лиги). */
        const typeIdsOf = (type: MapFilterType): number[] =>
            items.filter((it) => mapTypesWithLeague(it, league).includes(type)).map((it) => it.id);

        // Дерево одинаково устроено в обеих секциях: ветви → листья.
        // В фильтре листья — холодные чипы видимости, в розыгрыше — кнопки включения.
        const filterBranches: TreeBranch[] = [];
        const groupBranches: TreeBranch[] = [];

        const addTypeBranch = (key: string, label: string, types: MapFilterType[]): void => {
            if (types.length === 0) return;
            const hint = t('tools.random_type_toggle');
            filterBranches.push({
                key,
                label,
                hint,
                leaves: types.map((type) => ({
                    key: type,
                    label: t(`map_type.${type}` as any),
                    isActive: () => searchTypes.includes(type),
                    toggle: () => {
                        searchTypes = searchTypes.includes(type)
                            ? searchTypes.filter((x) => x !== type)
                            : [...searchTypes, type];
                    },
                })),
            });
            groupBranches.push({
                key,
                label,
                hint,
                leaves: types.map((type) => ({
                    key: type,
                    label: t(`map_type.${type}` as any),
                    ids: typeIdsOf(type),
                })),
            });
        };

        // Чистим выбор от значений, которых больше нет (после правки справочника/пресета)
        searchTypes = searchTypes.filter((type) => presentTypes.includes(type));
        searchCounts = searchCounts.filter((count) => counts.includes(count));
        const formats = TEAM_FORMATS.filter((format) =>
            items.some((it) => (it.teamFormats ?? []).includes(format))
        );
        searchFormats = searchFormats.filter((format) => formats.includes(format));

        const formatTypes = MAP_TYPES.filter((type) => presentTypes.includes(type));
        const styleTypes = MAP_STYLE_TYPES.filter((type) => presentTypes.includes(type));
        const leagueTypes: MapFilterType[] = presentTypes.includes(LEAGUE_MAP_TYPE) ? [LEAGUE_MAP_TYPE] : [];
        addTypeBranch('format', t('tools.random_types_format'), formatTypes);

        // Формат командной карты — отдельная ветвь: фильтр видимости и включение групп
        if (formats.length > 0) {
            const branchLabel = t('tools.random_types_teamformat');
            const branchHint = t('tools.random_formats_toggle');
            filterBranches.push({
                key: 'teamfmt',
                label: branchLabel,
                hint: branchHint,
                leaves: formats.map((format) => ({
                    key: format,
                    label: teamFormatLabel(format),
                    isActive: () => searchFormats.includes(format),
                    toggle: () => {
                        searchFormats = searchFormats.includes(format)
                            ? searchFormats.filter((x) => x !== format)
                            : [...searchFormats, format];
                    },
                })),
            });
            groupBranches.push({
                key: 'teamfmt',
                label: branchLabel,
                hint: branchHint,
                leaves: formats.map((format) => ({
                    key: format,
                    label: teamFormatLabel(format),
                    ids: items.filter((it) => (it.teamFormats ?? []).includes(format)).map((it) => it.id),
                })),
            });
        }

        addTypeBranch('style', t('tools.random_types_style'), styleTypes);
        addTypeBranch('league', t('tools.random_types_league'), leagueTypes);

        // Число игроков — отдельная ветвь того же дерева
        if (counts.length > 0) {
            const branchLabel = t('tools.random_types_players');
            const branchHint = t('tools.random_players_toggle');
            filterBranches.push({
                key: 'players',
                label: branchLabel,
                hint: branchHint,
                leaves: counts.map((count) => ({
                    key: `c${count}`,
                    label: String(count),
                    title: t('tools.random_players', { n: count }),
                    isActive: () => searchCounts.includes(count),
                    toggle: () => {
                        searchCounts = searchCounts.includes(count)
                            ? searchCounts.filter((x) => x !== count)
                            : [...searchCounts, count];
                    },
                })),
            });
            groupBranches.push({
                key: 'players',
                label: branchLabel,
                hint: branchHint,
                leaves: counts.map((count) => ({
                    key: `c${count}`,
                    label: String(count),
                    title: t('tools.random_players', { n: count }),
                    ids: items.filter((it) => it.playerCount === count).map((it) => it.id),
                })),
            });
        }

        /**
         * Дерево типов: ветвь сворачивается кликом по названию, листья — фильтры
         * (холодные) или включение в розыгрыш (тёплые). Переключаются только
         * листья: «вся ветвь сразу» намеренно не даём, чтобы не перегружать панель.
         */
        const buildTypeTree = (mode: 'filter' | 'group', branches: TreeBranch[]): HTMLElement => {
            const tree = document.createElement('div');
            tree.className = 'rr-tree';

            for (const branch of branches) {
                const branchEl = document.createElement('div');
                branchEl.className = 'rr-tree-branch';
                const collapseKey = `${mode}:${branch.key}`;
                if (collapsedTreeBranches.has(collapseKey)) branchEl.classList.add('is-collapsed');

                const head = document.createElement('div');
                head.className = 'rr-tree-head';

                const toggle = document.createElement('button');
                toggle.type = 'button';
                toggle.className = 'rr-tree-toggle';
                toggle.title = t('tools.random_tree_toggle');
                const arrow = document.createElement('span');
                arrow.className = 'rr-tree-arrow';
                const name = document.createElement('span');
                name.className = 'rr-tree-label';
                name.textContent = branch.label;
                toggle.append(arrow, name);
                toggle.addEventListener('click', () => {
                    const collapsed = branchEl.classList.toggle('is-collapsed');
                    if (collapsed) collapsedTreeBranches.add(collapseKey);
                    else collapsedTreeBranches.delete(collapseKey);
                });
                head.appendChild(toggle);

                const children = document.createElement('div');
                children.className = 'rr-tree-children';
                for (const leaf of branch.leaves) {
                    if (mode === 'filter' && leaf.isActive && leaf.toggle) {
                        children.appendChild(makeFilterChip(leaf.label, leaf.isActive, leaf.toggle, leaf.title));
                    } else if (mode === 'group' && leaf.ids && leaf.ids.length > 0) {
                        children.appendChild(makeGroupToggle(leaf.label, leaf.ids, branch.hint));
                    }
                }

                branchEl.append(head, children);
                tree.appendChild(branchEl);
            }
            return tree;
        };

        // Порядок в колонке настроек: сначала включение в розыгрыш, ниже фильтр,
        // в самом низу — настройки поиска (добавляются в конце, см. сборку body).
        if (groupBranches.length > 0) {
            // Легенда значков — чтобы состояния «вся группа / часть / выключена» читались сразу
            const legend = document.createElement('div');
            legend.className = 'rr-group-legend';
            legend.textContent = t('tools.random_groups_legend');
            groupSection.append(legend, buildTypeTree('group', groupBranches));
            tools.appendChild(groupSection);
        }

        if (filterBranches.length > 0) {
            filterSection.appendChild(buildTypeTree('filter', filterBranches));
            tools.appendChild(filterSection);
        }

        // --- 3. Список карт: выбранные сверху, остальные — ниже ---
        const buildGroup = (labelText: string): void => {
            const wrap = document.createElement('div');
            wrap.className = 'rr-map-group';
            const headEl = document.createElement('div');
            headEl.className = 'rr-map-group-head';
            const label = document.createElement('span');
            label.className = 'rr-map-group-label';
            label.textContent = labelText;
            const count = document.createElement('span');
            count.className = 'rr-map-group-count';
            headEl.append(label, count);
            const list = document.createElement('div');
            list.className = 'rr-map-group-list';
            wrap.append(headEl, list);
            chips.appendChild(wrap);
            mapGroups.push({ wrap, list, count });
        };
        buildGroup(t('tools.random_group_selected'));
        buildGroup(t('tools.random_group_others'));

        for (const it of sorted) {
            mapRows.push({
                id: it.id,
                row: buildMapRow(it, presets, () => {
                    const list = options.getExcluded();
                    const i = list.indexOf(it.id);
                    options.setExcluded(i >= 0 ? list.filter((id) => id !== it.id) : [...list, it.id]);
                    update();
                }),
            });
        }
        applyOrder();
    } else {
        for (const it of sorted) appendChip(chips, it);
    }

    allBtn.addEventListener('click', () => {
        const visible = new Set(items.filter(matches).map((it) => it.id));
        options.setExcluded(options.getExcluded().filter((id) => !visible.has(id)));
        update();
    });
    noneBtn.addEventListener('click', () => {
        const visible = items.filter(matches).map((it) => it.id);
        options.setExcluded([...new Set([...options.getExcluded(), ...visible])]);
        update();
    });

    if (items.length === 0) {
        chips.textContent = t('tools.random_empty');
    }

    box.append(head);

    if (isMap) {
        // Слева — настройки пула: «Включить в розыгрыш» → «Фильтр списка» → поиск
        // в самом низу; справа — крупный список карт.
        const body = document.createElement('div');
        body.className = 'rr-maps-body';
        const side = document.createElement('div');
        side.className = 'rr-maps-side';
        tools.appendChild(searchBar);
        side.append(tools);
        const listCol = document.createElement('div');
        listCol.className = 'rr-maps-list-col';
        listCol.append(buildViewSwitch(), chips, emptyEl);
        body.append(side, listCol);
        box.append(body);
    } else {
        // Моды — горизонтальная панель: поиск и чипы во всю ширину
        box.classList.add('rr-pool--mods');
        if (tools.childElementCount > 0) box.appendChild(tools);
        box.append(chips, emptyEl);
    }

    // Начальная отрисовка счётчика/состояний/видимости (без перерисовки барабана)
    applyStates();
    applyOrder();
    applyVisibility();

    return {
        el: box,
        refresh: () => {
            if (skipToggle) skipToggle.checked = skipOn();
            applyStates();
            applyOrder();
            applyVisibility();
        },
    };
}
